import React from 'react';
import { Tab, Box, alpha, Tooltip, Chip } from '@mui/material';
import { TabList } from '@mui/lab';
import { styled } from '@mui/material/styles';
import { isProjectExpired } from 'utils/project';
import { ProjectModel } from 'store/userOrg/userOrgApi';

interface ProjectTabsProps {
    value: string;
    onChange: (event: React.SyntheticEvent, newValue: string) => void;
    projects: (ProjectModel | string)[];
    renderLabel?: (project: ProjectModel | string) => React.ReactNode;
}

const StyledTab = styled(Tab)(({ theme }) => ({
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    minHeight: 48,
    borderRadius: theme.shape.borderRadius,
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        color: theme.palette.primary.main,
    },
    '&.Mui-selected': {
        color: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.12),
        fontWeight: 700,
    },
    '&.Mui-focusVisible': {
        backgroundColor: alpha(theme.palette.primary.main, 0.2),
    },
}));

const ExpiredTab = styled(StyledTab)(({ theme }) => ({
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.grey[200],
    '&:hover': {
        backgroundColor: theme.palette.grey[300],
        color: theme.palette.text.primary,
    },
    '&.Mui-selected': {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.grey[400],
    },
}));

export default function ProjectTabs({ value, onChange, projects, renderLabel }: ProjectTabsProps) {
    return (
        <Box sx={{ width: '100%' }}>
            <TabList
                onChange={onChange}
                variant='scrollable'
                scrollButtons='auto'
                aria-label='project tabs'
                sx={{
                    '& .MuiTabs-indicator': { display: 'none' },
                }}>
                {projects.map(project => {
                    const isString = typeof project === 'string';
                    const projectValue = isString ? project : project?.ShortName || '';

                    let labelContent: React.ReactNode = projectValue;
                    if (renderLabel) {
                        labelContent = renderLabel(project);
                    } else if (projectValue === '*') {
                        labelContent = 'All';
                    }

                    const expired = !isString && isProjectExpired(project);
                    const TabComponent = expired ? ExpiredTab : StyledTab;

                    let icon: React.ReactNode = null;
                    if (expired) {
                        icon = (
                            <Chip
                                label='Expired'
                                size='small'
                                color='warning'
                                sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    flexShrink: 0,
                                    cursor: 'inherit',
                                }}
                            />
                        );
                    }

                    const MAX_LENGTH = 20;
                    const isAll = projectValue === '*';
                    const shouldTruncate = !renderLabel && !isAll && projectValue.length > MAX_LENGTH;
                    const displayedLabel = shouldTruncate
                        ? `${projectValue.substring(0, MAX_LENGTH)}...`
                        : labelContent;

                    return (
                        <TabComponent
                            key={projectValue}
                            label={
                                <Tooltip title={projectValue} disableHoverListener={!shouldTruncate}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {displayedLabel}
                                        {icon}
                                    </Box>
                                </Tooltip>
                            }
                            value={projectValue}
                        />
                    );
                })}
            </TabList>
        </Box>
    );
}
